import { motion } from 'framer-motion';
import { type FC, useEffect, useState } from 'react';
import { GlobalLoader } from '@/app/components/shared/GlobalLoader';
import { cardItemVariants, cardsContainerVariants } from '@/app/components/shared/motion-variants';
import { trackClickButton } from '@/app/components/shared/TikTokPixel';
import { SITE } from '@/app/constants';
import { useGotoManageStripe } from '@/app/hooks';
import type { PeriodType, ProductList, ProductPlan, SubscriptionProductType, TabItemType } from '@/app/types';
import { cn } from '@/app/utils';
import { ProductEmptyState } from '../ProductEmptyState';
import { BonusCode } from './components/BonusCode';
import { ErrorComponent } from './components/Error';
import { PlanCard } from './components/PlanCard';
import { SubscriptionButton } from './components/SubscriptionButton';
import { Tab } from './components/Tab';

export const PlanDetail: FC<{
  source?: string;
  period?: PeriodType | null;
  setPeriod?: (period: PeriodType | null) => void;
  loadingProducts: boolean;
  products: ProductList | null;
  tabList: TabItemType[];
  error?: Error | null;
  bonusCodeProps?: string;
  setBonusCodeProps?: (bonusCode: string) => void;
  /** 弹窗来源：套餐弹窗打开时的 from_page，透传给订阅按钮以随 vip_price_click 上报 modal_from_page */
  modalFromPage?: string;
}> = ({
  source,
  period: externalPeriod,
  setPeriod: externalSetPeriod,
  loadingProducts,
  products,
  tabList,
  error,
  bonusCodeProps,
  setBonusCodeProps,
  modalFromPage,
}) => {
  const { loading, handleGotoManageStripe } = useGotoManageStripe();

  const [internalPeriod, setInternalPeriod] = useState<PeriodType | null>(null);
  const setPeriod = externalSetPeriod ?? setInternalPeriod;
  const period = internalPeriod ?? externalPeriod;

  useEffect(() => {
    if (!products || loadingProducts || !externalPeriod) return;
    SITE === 'global' && trackClickButton();
  }, [products, loadingProducts, externalPeriod]);

  const renderSubscriptionButton = (product: SubscriptionProductType) => {
    return (
      <SubscriptionButton
        source={source || ''}
        loading={loading}
        product={product}
        getManagement={handleGotoManageStripe}
        modalFromPage={modalFromPage}
      />
    );
  };

  /**
   * 计算"年付 vs 月付"节省金额（cent），用于卡片订阅按钮下方的提示。
   * 仅 yearly 周期且找得到同 plan 的 monthly 产品才返回正数。
   */
  const getMonthlyEquivalentSaved = (product: SubscriptionProductType): number => {
    if (product.billing_period !== 'yearly') return 0;
    const monthlyPeriod: PeriodType = product.product_type === 'subscription' ? 'subscription_monthly' : 'pass_monthly';
    const monthly = products?.[monthlyPeriod]?.[product.plan_type as keyof ProductPlan];
    if (!monthly) return 0;
    const saved = 12 * (monthly.price ?? 0) - (product.price ?? 0);
    return saved > 0 ? saved : 0;
  };

  if (error) return <ErrorComponent />;
  if (loadingProducts && !products)
    return (
      <div className="flex min-h-[400px] w-full items-center justify-center">
        <GlobalLoader className="static translate-x-0 translate-y-0" />
      </div>
    );
  if (!products || !period || tabList.length === 0) return <ProductEmptyState className="mt-10" />;

  return (
    <>
      <div className="select-none text-center">
        {tabList.length > 0 && period && (
          <div className="relative">
            <Tab setPeriod={setPeriod} period={period} tabList={tabList} />
            <BonusCode
              bonusCodeProps={bonusCodeProps}
              setBonusCodeProps={setBonusCodeProps}
              className="-translate-y-1/2 absolute top-1/2 right-0 sm:static sm:mt-3 sm:flex sm:w-full sm:translate-y-0 sm:justify-center"
            />
          </div>
        )}
      </div>

      <motion.div
        key={period}
        className={cn(
          'grid gap-4 sm:mt-0 sm:grid-cols-1 sm:px-4',
          // 弹窗场景隐藏免费版后仅 3 张卡片，用 3 列避免留空列
          source === 'dialog' ? 'grid-cols-3 md:grid-cols-2' : 'grid-cols-4 lg:grid-cols-2',
        )}
        initial="hidden"
        animate="visible"
        variants={cardsContainerVariants}
      >
        {Object.keys(products[period] || {}).map((key) => {
          const product = products[period]?.[key as keyof ProductPlan];
          if (!product) return null;
          // 弹窗场景不展示免费版卡片
          if (source === 'dialog' && key === 'free') return null;
          const isDiscount = tabList.find((v) => v.value === period)?.isDiscount;
          return (
            <motion.div key={`${key}_${period}`} variants={cardItemVariants}>
              <PlanCard
                isDiscount={isDiscount}
                product={product}
                renderSubscriptionButton={renderSubscriptionButton}
                monthlyEquivalentSaved={getMonthlyEquivalentSaved(product)}
              />
            </motion.div>
          );
        })}
      </motion.div>
    </>
  );
};
